/**
 * useCozyAnimation.js
 * ─────────────────────────────────────────────────────────────────
 * Presets d'animation centralisés pour ColoBongo.
 * Principes : douces, naturelles, jamais agressives, mobile-first.
 * Tous les presets respectent automatiquement prefers-reduced-motion
 * via le hook useReducedMotion de Framer Motion.
 *
 * Usage :
 *   const { variants, transition, spring } = useCozyAnimation();
 *   <motion.div variants={variants.fadeUp} initial="hidden" animate="show" />
 * ─────────────────────────────────────────────────────────────────
 */

import { useReducedMotion } from 'framer-motion';

export function useCozyAnimation() {
  const reduce = useReducedMotion();

  // ─── Transitions de base ────────────────────────────────────────

  /** Transition douce pour la majorité des éléments */
  const ease = {
    duration: reduce ? 0 : 0.38,
    ease: [0.25, 0.46, 0.45, 0.94],
  };

  /** Spring naturel — entrées, cartes, modals */
  const spring = {
    type: 'spring',
    damping: reduce ? 999 : 22,
    stiffness: reduce ? 999 : 260,
    mass: 0.8,
  };

  /** Spring léger — micro-interactions, boutons */
  const springLight = {
    type: 'spring',
    damping: reduce ? 999 : 18,
    stiffness: reduce ? 999 : 320,
    mass: 0.6,
  };

  /** Spring lent — éléments héroïques, célébrations */
  const springSlow = {
    type: 'spring',
    damping: reduce ? 999 : 14,
    stiffness: reduce ? 999 : 180,
    mass: 1,
  };

  // ─── Variants réutilisables ─────────────────────────────────────

  const variants = {

    /**
     * fadeUp — entrée standard d'un élément depuis le bas
     * Usage : cards, sections, modals
     */
    fadeUp: {
      hidden: { opacity: 0, y: reduce ? 0 : 18 },
      show:   { opacity: 1, y: 0, transition: ease },
      exit:   { opacity: 0, y: reduce ? 0 : -10, transition: { ...ease, duration: 0.22 } },
    },

    /**
     * fadeIn — apparition simple sans déplacement
     * Usage : overlays, tooltips, états vides
     */
    fadeIn: {
      hidden: { opacity: 0 },
      show:   { opacity: 1, transition: ease },
      exit:   { opacity: 0, transition: { ...ease, duration: 0.2 } },
    },

    /**
     * scaleIn — apparition avec légère mise à l'échelle
     * Usage : modals, popovers, badges de score
     */
    scaleIn: {
      hidden: { opacity: 0, scale: reduce ? 1 : 0.88 },
      show:   { opacity: 1, scale: 1, transition: spring },
      exit:   { opacity: 0, scale: reduce ? 1 : 0.92, transition: { ...ease, duration: 0.2 } },
    },

    /**
     * slideFromBottom — glissement depuis le bas de l'écran
     * Usage : bottom sheets, panneaux de résultat
     */
    slideFromBottom: {
      hidden: { opacity: 0, y: reduce ? 0 : '100%' },
      show:   { opacity: 1, y: 0, transition: { ...spring, damping: reduce ? 999 : 26 } },
      exit:   { opacity: 0, y: reduce ? 0 : '100%', transition: ease },
    },

    /**
     * bongoEntrance — Bongo saute dans la scène
     * Usage : apparition de Bongo lors d'un résultat ou d'une célébration
     */
    bongoEntrance: {
      hidden: { opacity: 0, scale: reduce ? 1 : 0.5, y: reduce ? 0 : 30 },
      show: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: springSlow,
      },
    },

    /**
     * staggerContainer — parent d'une liste à entrée progressive
     * Usage : galerie Frigo, liste de colorages, leçons
     */
    staggerContainer: {
      hidden: {},
      show: {
        transition: {
          staggerChildren: reduce ? 0 : 0.07,
          delayChildren: reduce ? 0 : 0.05,
        },
      },
    },

    /**
     * staggerItem — enfant d'un staggerContainer
     * Toujours utiliser en combinaison avec staggerContainer
     */
    staggerItem: {
      hidden: { opacity: 0, y: reduce ? 0 : 14 },
      show:   { opacity: 1, y: 0, transition: ease },
    },

    /**
     * pageEnter — transition d'entree de page
     * Usage : dans CozyPageWrapper
     * Note : le filter blur a ete retire deliberement. Tout filter != none (y compris blur(0))
     * cree un containing block CSS pour les descendants position:fixed (spec W3C).
     * Safari/iOS ne l'optimise pas. OnboardingCarousel (position:fixed, inset:0) devenait
     * invisible car ancre au motion.div de hauteur zero plutot qu'au viewport.
     * L'animation opacity+y est suffisante pour le polish d'entree de page.
     */
    pageEnter: {
      hidden: {
        opacity: 0,
        y: reduce ? 0 : 8,
      },
      show: {
        opacity: 1,
        y: 0,
        transition: {
          duration: reduce ? 0.18 : 0.24,
          ease: [0.23, 1, 0.32, 1],
        },
      },
      exit: {
        opacity: 0,
        y: reduce ? 0 : -4,
        transition: {
          duration: reduce ? 0.12 : 0.18,
          ease: [0.23, 1, 0.32, 1],
        },
      },
    },
  };

  // ─── Presets whileHover / whileTap ──────────────────────────────

  /** Feedback tactile doux pour les boutons principaux */
  const tapScale = {
    whileTap:  { scale: reduce ? 1 : 0.96 },
    whileHover: { scale: reduce ? 1 : 1.03 },
    transition: springLight,
  };

  /** Feedback pour les cartes (galerie, résultats) */
  const cardPress = {
    whileTap:  { scale: reduce ? 1 : 0.975, boxShadow: '0 2px 8px rgba(90,70,58,0.08)' },
    whileHover: { scale: reduce ? 1 : 1.015, boxShadow: '0 8px 24px rgba(232,132,92,0.18)' },
    transition: springLight,
  };

  /** Légère pulsation idle — pour attirer l'attention sans agresser */
  const gentlePulse = reduce
    ? {}
    : {
        animate: { scale: [1, 1.025, 1] },
        transition: { duration: 2.8, ease: 'easeInOut', repeat: Infinity },
      };

  // ─── Helpers spécifiques à ColoBongo ────────────────────────────

  /**
   * scoreReveal — animation du score IA (cercle de progression)
   * Retourne les props animate pour un SVG strokeDashoffset
   * @param {number} score   — score entre 0 et 100
   * @param {number} radius  — rayon du cercle SVG (défaut 40)
   */
  function scoreReveal(score, radius = 40) {
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (score / 100) * circumference;
    return {
      initial: { strokeDashoffset: circumference, opacity: 0 },
      animate: {
        strokeDashoffset: reduce ? offset : offset,
        opacity: 1,
        transition: {
          strokeDashoffset: { duration: reduce ? 0 : 1.2, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.3 },
          opacity: { duration: 0.3 },
        },
      },
      strokeDasharray: circumference,
    };
  }

  return {
    variants,
    ease,
    spring,
    springLight,
    springSlow,
    tapScale,
    cardPress,
    gentlePulse,
    scoreReveal,
    reduce,
  };
}
